// React
import {
  type AnchorHTMLAttributes,
  type HTMLAttributes,
  type LiHTMLAttributes,
  type OlHTMLAttributes,
  type Ref,
} from "react"

// Components
import { Slot } from "@radix-ui/react-slot"
import { BreadcrumbChevronIcon } from "@/react/components/icons/BreadcrumbChevronIcon"

// Hooks and utilities
import { cn } from "@/lib/utils"
import { UNDERLINE_SLIDE_IN } from "./link-underline"

/** Shipped breadcrumbs keep Figma's gap-4 but render 14px text instead of the primitive's 15px/1.5. */
export const BREADCRUMB_LIST_APP_CLASS = "flex-nowrap text-sm leading-[1.4]"

function Breadcrumb({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> }) {
  return (
    <nav
      ref={ref}
      aria-label="breadcrumb"
      className={cn("inline-flex items-center", className)}
      {...props}
    />
  )
}

function BreadcrumbList({
  className,
  ref,
  ...props
}: OlHTMLAttributes<HTMLOListElement> & { ref?: Ref<HTMLOListElement> }) {
  return (
    <ol
      ref={ref}
      className={cn(
        "flex flex-wrap items-center gap-4 text-[15px] leading-[1.5] font-inter font-normal text-grey",
        className,
      )}
      {...props}
    />
  )
}

function BreadcrumbItem({
  className,
  ref,
  ...props
}: LiHTMLAttributes<HTMLLIElement> & { ref?: Ref<HTMLLIElement> }) {
  return (
    <li
      ref={ref}
      className={cn("inline-flex items-center gap-2", className)}
      {...props}
    />
  )
}

function BreadcrumbLink({
  className,
  asChild = false,
  ref,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  asChild?: boolean
  ref?: Ref<HTMLAnchorElement>
}) {
  const Comp = asChild ? Slot : "a"
  return (
    <Comp
      ref={ref}
      className={cn(
        "inline-block",
        "focus-visible:outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring",
        UNDERLINE_SLIDE_IN,
        className,
      )}
      {...props}
    />
  )
}

function BreadcrumbPage({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { ref?: Ref<HTMLSpanElement> }) {
  return (
    <span
      ref={ref}
      aria-current="page"
      className={cn("font-normal text-off-black", className)}
      {...props}
    />
  )
}

function BreadcrumbSeparator({
  children,
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLLIElement> & { ref?: Ref<HTMLLIElement> }) {
  return (
    <li
      ref={ref}
      role="presentation"
      aria-hidden="true"
      className={cn("inline-flex items-center text-off-black", className)}
      {...props}
    >
      {children ?? <BreadcrumbChevronIcon />}
    </li>
  )
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
}
